<template>
    <app-layout>
        <template v-slot:header>
            <Header :title="$t('certificate_application_bulk_ebys_send')"
                    :isColumns="true"
                    @filter-div-status="datatable.filterStatus = $event">
                <template v-slot:columns>
                    <div class="mb-1" v-for="(column, key) in datatable.columns" :key="key">
                        <b-form-checkbox
                            :id="'checkbox-' + key"
                            :name="'checkbox-' + key"
                            :value="false"
                            :unchecked-value="true"
                            v-model="column.hidden"
                            v-if="column.field !== 'buttons'"
                        >
                            {{ column.label }}
                        </b-form-checkbox>
                    </div>
                </template>
            </Header>
        </template>
        <template v-slot:header-mobile>
            <HeaderMobile :title="$t('certificate_application_bulk_ebys_send')"
                          :isColumns="true"
                          @filter-div-status="datatable.filterStatus = $event">
                <template v-slot:columns>
                    <div class="mb-1" v-for="(column, key) in datatable.columns" :key="key">
                        <b-form-checkbox
                            :id="'checkbox-mobile-' + key"
                            :name="'checkbox-mobile-' + key"
                            :value="false"
                            :unchecked-value="true"
                            v-model="column.hidden"
                            v-if="column.field !== 'buttons'"
                        >
                            {{ column.label }}
                        </b-form-checkbox>
                    </div>
                </template>
            </HeaderMobile>
        </template>

        <div>
            <!-- Filtre Alanı -->
            <datatable-filter
                v-show="datatable.filterStatus"
                @filter="filter"
                @filterClear="filterClear"
            >
                <b-row>
                    <b-col sm="3" md="3">
                        <b-form-group :label="$t('certificate')">
                            <certificate-center-certificate-selectbox
                                v-model="datatable.queryParams.filter.certificate_id"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col sm="3" md="3">
                        <b-form-group :label="$t('certificate_persons')">
                            <certificate-person-selectbox
                                v-model="datatable.queryParams.filter.certificate_person_id"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col sm="3" md="3">
                        <b-form-group :label="$t('status')">
                            <status-selectbox
                                v-model="datatable.queryParams.filter.status"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col sm="3" md="3">
                        <b-form-group :label="$t('ebys_status')">
                            <multi-selectbox :allow-empty="true" :multiple="false"
                                             :options="ebysStatusOptions"
                                             v-model="datatable.queryParams.filter.ebys_status"></multi-selectbox>
                        </b-form-group>
                    </b-col>
                    <b-col sm="3" md="3">
                        <b-form-group :label="$t('certificate_date_between')" class="mb-0">
                            <v-date-picker v-model="localDateFilter" is-range>
                                <template v-slot="{ inputValue, inputEvents }">
                                    <div class="d-flex align-items-center">
                                        <div class="flex-grow-1">
                                            <b-form-group class="m-0 mr-1">
                                                <input :value="inputValue.start" v-on="inputEvents.start" :placeholder="$t('start_date')" class="form-control" />
                                            </b-form-group>
                                        </div>
                                        <div class="flex-grow-1">
                                            <b-form-group class="m-0 ml-1">
                                                <input :value="inputValue.end" v-on="inputEvents.end" :placeholder="$t('end_date')" class="form-control" />
                                            </b-form-group>
                                        </div>
                                    </div>
                                </template>
                            </v-date-picker>
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable
                :isLoading.sync="datatable.isLoading"
                :columns="datatable.columns"
                :rows="datatable.rows"
                :total="datatable.total"
                :queryParams="datatable.queryParams"
                :lineNumbers="false"
                :show-paginate="true"
                :select-options="!!datatable.rows.length"
                :selected-action-button-text="isSending ? $t('loading') : $t('ebys_send').toUpper()"
                @selected-bulk-action="bulkSendEbys"
                @on-page-change="onPageChange"
                @on-sort-change="onSortChange"
                @on-per-page-change="onPerPageChange"
                @on-selected-rows-change="selectedRows"
                v-show="datatable.showTable"
            />

            <!-- Sonuç Modalı -->
            <CommonModal ref="resultModal" size="lg">
                <template v-slot:CommonModalTitle>
                    {{ $t('process_results').toUpper() }}
                </template>
                <template v-slot:CommonModalContent>
                    <div class="table-responsive">
                        <table class="table table-bordered table-striped">
                            <thead>
                                <tr>
                                    <th class="bg-light">ID</th>
                                    <th class="bg-light">{{ $t('status') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in resultData" :key="index">
                                    <td class="align-middle">#{{ item.id }}</td>
                                    <td class="align-middle">
                                        <b-badge :variant="item.status === 'SUCCESS' ? 'success' : 'danger'" class="px-3 py-2 text-uppercase">
                                            {{ item.status }}
                                        </b-badge>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-slot:CommonModalFooter>
                    <b-button variant="secondary" @click="$refs.resultModal.$refs.commonModal.hide()">
                        {{ $t('close') }}
                    </b-button>
                </template>
            </CommonModal>
        </div>
    </app-layout>
</template>

<script>
// Layout
import AppLayout from '@/layouts/AppLayout';
import Header from '@/layouts/AppLayout/Header';
import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

// Components
import DatatableFilter from '@/components/datatable/DatatableFilter';
import Datatable from '@/components/datatable/Datatable';
import CommonModal from '@/components/elements/CommonModal';
import CertificatePersonSelectbox from '@/components/interactive-fields/CertificatePersonSelectbox';
import CertificateCenterCertificateSelectbox from '@/components/interactive-fields/CertificateCenterCertificateSelectbox';
import StatusSelectbox from '@/components/interactive-fields/StatusSelectbox';
import MultiSelectbox from '@/components/interactive-fields/MultiSelectbox';

// Service
import CertificateApplicationService from '@/services/CertificateApplicationService';

// Other
import qs from 'qs';
import moment from "moment/moment";

export default {
    name: 'CertificateApplicationBulkEbys',

    components: {
        AppLayout,
        Header,
        HeaderMobile,
        DatatableFilter,
        Datatable,
        CommonModal,
        CertificatePersonSelectbox,
        CertificateCenterCertificateSelectbox,
        StatusSelectbox,
        MultiSelectbox
    },

    metaInfo() {
        return {
            title: this.$t('certificate_application_bulk_ebys_send')
        };
    },

    data() {
        return {
            selectedData: [],
            resultData: [],
            isSending: false,
            ebysStatusOptions: [
                {value: 'waiting', text: this.$t('ebys_status_waiting')},
                {value: 'ready', text: this.$t('ebys_status_ready')},
            ],
            datatable: {
                isLoading: false,
                showTable: false,
                filterStatus: true,
                rows: [],
                total: 0,
                queryParams: {
                    filter: {},
                    sort: 'id',
                    page: 1,
                    limit: 20
                },
                columns: [
                    {
                        label: this.$t('certificate'),
                        field: 'certificate_id',
                        hidden: false,
                        tdClass: 'align-middle',
                        formatFn: (value, row) => {
                            return row.certificate ? row.certificate.name : '-';
                        }
                    },
                    {
                        label: this.toUpperCase('certificate_persons'),
                        field: 'certificate_person_id',
                        hidden: false,
                        tdClass: 'align-middle',
                        formatFn: (value, row) => {
                            return row.person
                                ? row.person.name + ' ' + row.person.surname
                                : '-';
                        }
                    },
                    {
                        label: this.toUpperCase('status'),
                        field: 'status',
                        hidden: false,
                        tdClass: 'align-middle',
                        formatFn: (row) => {
                            return row === 'a' ? this.$t('active') : this.$t('passive');
                        }
                    },
                    {
                        label: this.toUpperCase('ebys_status'),
                        field: 'ebys_status',
                        hidden: false,
                        tdClass: 'align-middle',
                        formatFn: (value) => {
                            if (value === 'ready') return this.$t('ebys_status_ready');
                            if (value === 'waiting') return this.$t('ebys_status_waiting');
                            return value || '';
                        }
                    },
                ],
            },
            localDateFilter: {
                start: null,
                end: null
            },

        };
    },

    methods: {
        filter() {
            this.datatable.queryParams.page = 1;
            this.getRows();
        },

        filterClear() {
            this.datatable.queryParams.filter = {};
            this.localDateFilter = {};
            this.getRows();
        },
        onPageChange(params) {
            this.datatable.queryParams.page = params.currentPage;
            this.getRows();
        },

        onPerPageChange(params) {
            this.datatable.queryParams.limit = params.currentPerPage;
            this.datatable.queryParams.page = 1;
            this.getRows();
        },

        onSortChange(params) {
            const sortType = params[0].type === 'desc' ? '-' : '';
            this.datatable.queryParams.sort = sortType + params[0].field;
            this.getRows();
        },

        getRows() {
            this.datatable.isLoading = true;
            this.datatable.showTable = true;
            this.selectedData = [];

            if (this.localDateFilter && this.localDateFilter.start && this.localDateFilter.end) {
                this.datatable.queryParams.filter.created_at_greater_than = moment(this.localDateFilter.start).format("YYYY-MM-DD");
                this.datatable.queryParams.filter.created_at_smaller_than = moment(this.localDateFilter.end).format("YYYY-MM-DD");
            }

            const config = {
                params: { ...this.datatable.queryParams },
                paramsSerializer: (params) => qs.stringify(params, { encode: false })
            };

            return CertificateApplicationService.getAll(config)
                .then((response) => {
                    this.datatable.rows = response.data.data;
                    this.datatable.total = response.data.pagination.total;
                })
                .catch((err) => {
                    this.showErrors(err);
                })
                .finally(() => {
                    this.datatable.isLoading = false;
                });
        },

        selectedRows(data) {
            if (data != null) {
                this.selectedData = data.selectedRows;
            }
        },

        getSelectedIds() {
            return this.selectedData.map((row) => row.id);
        },

        bulkSendEbys() {
            const ids = this.getSelectedIds();

            if (!ids.length) {
                return this.$toast.error(this.$t('not_selected'));
            }

            this.$swal.fire({
                text: this.$t('approve_or_reject_confirm'),
                showCancelButton: true,
                confirmButtonText: this.$t('yes'),
                cancelButtonText: this.$t('no'),
            }).then((result) => {
                if (!result.isConfirmed) return;

                this.isSending = true;

                CertificateApplicationService.bulkSendEbys({ ids })
                    .then((response) => {
                        this.$toast.success(this.$t('proccess_success'));
                        this.resultData = response.data.data || [];
                        this.$refs.resultModal.$refs.commonModal.show();
                        this.selectedData = [];
                        this.getRows();
                    })
                    .catch((err) => {
                        this.showErrors(err);
                    })
                    .finally(() => {
                        this.isSending = false;
                    });
            });
        }
    }
};
</script>
